import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X, Users, Mail, Shield, ChevronDown, Copy, Check,
  Trash2, Settings, Eye, Edit3, Lock, UserPlus, UserCog,
  Upload, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../App';
import { getOrgMembers, getOrgById, getUserPermissions } from '../lib/supabase';
import { can } from '../lib/permissions';
import './TeamSettings.css';

const ROLE_LABELS = { admin: 'Admin', member: 'Member', viewer: 'Viewer', custom: 'Custom' };
const ROLE_COLORS = { admin: '#111', member: '#3b5bdb', viewer: '#666', custom: '#7c3aed' };

const CUSTOM_PERMISSIONS = [
  { key: 'view_documents', label: 'View Documents' },
  { key: 'view_deals', label: 'View Deals' },
  { key: 'edit', label: 'Edit Content' },
  { key: 'save', label: 'Save / Export' },
  { key: 'download', label: 'Download Files' },
  { key: 'import', label: 'Import Data' },
  { key: 'add_members', label: 'Add Members' },
  { key: 'audit_trail', label: 'View Audit Trail' },
];

const DEFAULT_ROLE_PERMS = {
  admin:  { view_documents: true,  view_deals: true,  edit: true,  save: true,  download: true,  import: true,  add_members: true,  audit_trail: true  },
  member: { view_documents: true,  view_deals: true,  edit: true,  save: true,  download: true,  import: true,  add_members: false, audit_trail: false },
  viewer: { view_documents: true,  view_deals: true,  edit: false, save: false, download: false, import: false, add_members: false, audit_trail: false },
  custom: { view_documents: false, view_deals: false,  edit: false, save: false, download: false, import: false, add_members: false, audit_trail: false },
};

const TeamSettings = ({ currentUser, onClose }) => {
  const [activeTab, setActiveTab] = useState('members');
  const [members, setMembers] = useState([]);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Invite state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [recentInvites, setRecentInvites] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);

  // CSV bulk invite state
  const csvInputRef = useRef(null);
  const [csvRows, setCsvRows] = useState([]); // [{ email, role, status: null|'sending'|'done'|'error', error }]
  const [csvSending, setCsvSending] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);

  // Permissions tab state
  const [permView, setPermView] = useState('roles'); // 'roles' | 'members'
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [memberPermissions, setMemberPermissions] = useState([]);
  const [permLoading, setPermLoading] = useState(false);
  const [permSaving, setPermSaving] = useState(false);

  // Role-level permissions
  const [rolePerms, setRolePerms] = useState(DEFAULT_ROLE_PERMS);
  const [editingRole, setEditingRole] = useState('member');
  const [rolePermsSaving, setRolePermsSaving] = useState(false);

  const isAdmin = currentUser?.role === 'admin';
  const canAddMembers = isAdmin || can(currentUser, 'add_members');

  const loadData = useCallback(async () => {
    if (!currentUser?.orgId) {
      setLoading(false);
      setError('You must belong to an organization to manage team settings.');
      return;
    }
    try {
      const [membersData, orgData] = await Promise.all([
        getOrgMembers(currentUser.orgId),
        getOrgById(currentUser.orgId),
      ]);
      setMembers(membersData);
      setOrg(orgData);
      setError('');
    } catch (err) {
      setError('Failed to load team data.');
    } finally {
      setLoading(false);
    }
    // Also load pending invites (best-effort)
    try {
      const res = await axios.get(`${API_URL}/api/org/invites`, { withCredentials: true });
      setPendingInvites(res.data || []);
    } catch (_) {}
  }, [currentUser]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load role permissions
  useEffect(() => {
    if (!currentUser?.orgId) return;
    axios.get(`${API_URL}/api/org/role-permissions`)
      .then(res => setRolePerms(prev => ({ ...prev, ...res.data })))
      .catch(() => {});
  }, [currentUser]);

  const loadMemberPermissions = async (userId) => {
    setPermLoading(true);
    setSelectedMemberId(userId);
    try {
      const perms = await getUserPermissions(userId);
      setMemberPermissions(perms);
    } catch {
      setMemberPermissions([]);
    } finally {
      setPermLoading(false);
    }
  };

  const getPermValue = (resource, action) => {
    const perm = memberPermissions.find(p => p.resource === resource && p.action === action);
    return perm?.allowed ?? false;
  };

  const togglePerm = (resource, action) => {
    setMemberPermissions(prev => {
      const existing = prev.find(p => p.resource === resource && p.action === action);
      if (existing) {
        return prev.map(p =>
          p.resource === resource && p.action === action ? { ...p, allowed: !p.allowed } : p
        );
      }
      return [...prev, { user_id: selectedMemberId, resource, action, allowed: true }];
    });
  };

  const savePermissions = async () => {
    setPermSaving(true);
    try {
      await axios.put(`${API_URL}/api/org/permissions/${selectedMemberId}`, { permissions: memberPermissions });
    } catch {
      setError('Failed to save permissions.');
    } finally {
      setPermSaving(false);
    }
  };

  const saveRolePermissions = async (roleType) => {
    setRolePermsSaving(true);
    try {
      await axios.put(`${API_URL}/api/org/role-permissions`, {
        roleType,
        permissions: rolePerms[roleType],
      });
    } catch {
      setError('Failed to save role permissions.');
    } finally {
      setRolePermsSaving(false);
    }
  };

  const toggleRolePerm = (roleType, key) => {
    setRolePerms(prev => ({
      ...prev,
      [roleType]: { ...prev[roleType], [key]: !prev[roleType][key] },
    }));
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteLoading(true);
    setInviteError('');
    try {
      const res = await axios.post(`${API_URL}/api/org/invite`, { email: inviteEmail.trim(), role: inviteRole });
      const inviteLink = `${window.location.origin}/invite/${res.data.token}`;
      const emailSent = res.data.emailSent;
      setRecentInvites(prev => [{
        email: inviteEmail.trim(),
        role: inviteRole,
        link: inviteLink,
        emailSent
      }, ...prev]);
      setInviteEmail('');
      // Refresh member list + pending invites to update counts
      loadData();
    } catch (err) {
      setInviteError(err.response?.data?.error || 'Failed to send invite.');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await axios.delete(`${API_URL}/api/org/invites/${inviteId}`, { withCredentials: true });
      setPendingInvites(prev => prev.filter(i => i.id !== inviteId));
    } catch {
      setError('Failed to revoke invite.');
    }
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const lines = ev.target.result.split('\n').map(l => l.trim()).filter(Boolean);
      const parsed = [];
      for (const line of lines) {
        const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
        const email = cols[0];
        if (!email || email.toLowerCase() === 'email') continue; // skip header
        const role = ['admin', 'member', 'viewer'].includes((cols[1] || '').toLowerCase())
          ? cols[1].toLowerCase() : 'member';
        parsed.push({ email, role, status: null, error: null });
      }
      setCsvRows(parsed);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleSendAllInvites = async () => {
    if (!csvRows.length || csvSending) return;
    setCsvSending(true);
    const updated = [...csvRows];
    for (let i = 0; i < updated.length; i++) {
      if (updated[i].status === 'done') continue;
      updated[i] = { ...updated[i], status: 'sending' };
      setCsvRows([...updated]);
      try {
        const res = await axios.post(`${API_URL}/api/org/invite`, {
          email: updated[i].email,
          role: updated[i].role
        });
        const link = `${window.location.origin}/invite/${res.data.token}`;
        updated[i] = { ...updated[i], status: 'done', link };
        setRecentInvites(prev => [{ email: updated[i].email, role: updated[i].role, link, emailSent: res.data.emailSent }, ...prev]);
      } catch (err) {
        updated[i] = { ...updated[i], status: 'error', error: err.response?.data?.error || 'Failed' };
      }
      setCsvRows([...updated]);
    }
    setCsvSending(false);
  };

  const handleCopyLink = (link, idx) => {
    navigator.clipboard.writeText(link);
    setCopiedToken(idx);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleChangeRole = async (memberId, newRole) => {
    try {
      const member = members.find(m => m.id === memberId);
      await axios.patch(`${API_URL}/api/org/members/${memberId}/role`, { role: newRole });
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, role: newRole } : m));
      // Audit log
      try {
        await axios.post(`${API_URL}/api/audit/log`, {
          action: 'role_changed',
          resource: 'members',
          details: `Changed ${member?.full_name || member?.email}'s role to ${newRole}`,
        }, { withCredentials: true });
      } catch (_) {}
    } catch {
      setError('Failed to update role.');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Remove this member from the organization?')) return;
    try {
      await axios.delete(`${API_URL}/api/org/members/${memberId}`);
      setMembers(prev => prev.filter(m => m.id !== memberId));
    } catch {
      setError('Failed to remove member.');
    }
  };

  const isInline = !onClose;

  const inner = (
    <>
      {/* Header — only shown in modal mode */}
      {!isInline && (
        <div className="team-modal-header">
          <div className="team-modal-title">
            <Users size={20} />
            <div>
              <h2>{org?.name || 'Team Settings'}</h2>
              {org && <span className="org-type-tag">{org.type?.replace(/_/g, ' ')}</span>}
            </div>
          </div>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>
      )}

      {/* Tabs */}
      <div className="team-tabs">
          <button className={`team-tab ${activeTab === 'members' ? 'active' : ''}`} onClick={() => setActiveTab('members')}>
            <Users size={15} /> Members {members.length + pendingInvites.length > 0 && <span style={{ marginLeft: 4, background: '#E5E7EB', color: '#374151', borderRadius: 10, fontSize: 11, padding: '1px 6px' }}>{members.length + pendingInvites.length}</span>}
          </button>
          {canAddMembers && (
            <button className={`team-tab ${activeTab === 'invite' ? 'active' : ''}`} onClick={() => setActiveTab('invite')}>
              <Mail size={15} /> Invite
            </button>
          )}
          {isAdmin && (
            <button className={`team-tab ${activeTab === 'permissions' ? 'active' : ''}`} onClick={() => setActiveTab('permissions')}>
              <Shield size={15} /> Permissions
            </button>
          )}
        </div>

        <div className="team-modal-body">
          {error && <div className="team-error">{error}</div>}

          {/* ── Members Tab ── */}
          {activeTab === 'members' && (
            <div>
              {loading ? (
                <div className="team-loading">Loading members...</div>
              ) : members.length === 0 ? (
                <div className="team-empty">
                  <p>No members found.</p>
                  {isAdmin && (
                    <button
                      className="invite-members-btn"
                      onClick={() => setActiveTab('invite')}
                    >
                      <UserPlus size={16} /> Invite Members
                    </button>
                  )}
                </div>
              ) : (
                <div className="members-list">
                  {members.map(member => (
                    <div key={member.id} className="member-row">
                      <div className="member-avatar">
                        {(member.full_name || member.email)?.[0]?.toUpperCase()}
                      </div>
                      <div className="member-info">
                        <span className="member-name">{member.full_name || member.email}</span>
                        <span className="member-email">{member.email}</span>
                      </div>
                      {isAdmin && member.id !== currentUser.id ? (
                        <div className="member-actions">
                          <div className="role-select-wrapper">
                            <select
                              value={member.role}
                              onChange={e => handleChangeRole(member.id, e.target.value)}
                              className="role-select"
                            >
                              <option value="admin">Admin</option>
                              <option value="member">Member</option>
                              <option value="viewer">Viewer</option>
                              <option value="custom">Custom</option>
                            </select>
                            <ChevronDown size={12} className="role-chevron" />
                          </div>
                          <button
                            className="remove-btn"
                            onClick={() => handleRemoveMember(member.id)}
                            title="Remove member"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ) : (
                        <span
                          className="role-badge"
                          style={{ background: (ROLE_COLORS[member.role] || '#666') + '15', color: ROLE_COLORS[member.role] || '#666' }}
                        >
                          {ROLE_LABELS[member.role] || member.role}
                          {member.id === currentUser.id && ' (You)'}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* Pending invites section */}
              {pendingInvites.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <h4 style={{ fontSize: 13, fontWeight: 600, color: '#6B7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Pending Invites ({pendingInvites.length})
                  </h4>
                  <div className="members-list">
                    {pendingInvites.map(invite => (
                      <div key={invite.id} className="member-row">
                        <div className="member-avatar" style={{ background: '#FEF3C7', color: '#D97706' }}>
                          {invite.email?.[0]?.toUpperCase()}
                        </div>
                        <div className="member-info">
                          <span className="member-name">{invite.email}</span>
                          <span className="member-email" style={{ color: '#F59E0B' }}>Invite pending</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="role-badge" style={{ background: '#FEF3C7', color: '#D97706' }}>
                            {ROLE_LABELS[invite.role] || invite.role}
                          </span>
                          {isAdmin && (
                            <button className="remove-btn" onClick={() => handleRevokeInvite(invite.id)} title="Revoke invite">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Invite button at bottom for admins */}
              {isAdmin && members.length > 0 && (
                <div className="members-footer">
                  <button
                    className="invite-members-btn"
                    onClick={() => setActiveTab('invite')}
                  >
                    <UserPlus size={16} /> Invite Members
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Invite Tab ── */}
          {activeTab === 'invite' && canAddMembers && (
            <div className="invite-tab">
              <p className="invite-tab-desc">
                Send an invite link to add new members to <strong>{org?.name || 'your organization'}</strong>.
              </p>

              {inviteError && <div className="team-error">{inviteError}</div>}

              <form onSubmit={handleSendInvite} className="invite-tab-form">
                <div className="invite-tab-row">
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    placeholder="colleague@company.com"
                    className="invite-tab-input"
                    required
                  />
                  <select
                    value={inviteRole}
                    onChange={e => setInviteRole(e.target.value)}
                    className="invite-tab-role"
                  >
                    <option value="admin">Admin</option>
                    <option value="member">Member</option>
                    <option value="viewer">Viewer</option>
                    <option value="custom">Custom</option>
                  </select>
                  <button type="submit" className="invite-tab-btn" disabled={inviteLoading || !currentUser?.orgId}>
                    {inviteLoading ? '...' : 'Generate Link'}
                  </button>
                </div>
              </form>

              {/* CSV bulk invite */}
              <div className="csv-invite-section">
                <div className="csv-invite-header">
                  <span className="csv-invite-title">Bulk invite via CSV</span>
                  <span className="csv-invite-hint">Two columns: <code>email, role</code> — role is optional (defaults to member)</span>
                </div>
                <button className="csv-upload-btn" onClick={() => csvInputRef.current?.click()}>
                  <Upload size={14} /> Upload CSV
                </button>
                <input ref={csvInputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={handleCsvUpload} />

                {csvRows.length > 0 && (
                  <div className="csv-preview">
                    <table className="csv-table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvRows.map((row, i) => (
                          <tr key={i} className={`csv-row csv-row-${row.status || 'pending'}`}>
                            <td>{row.email}</td>
                            <td>
                              <select
                                value={row.role}
                                disabled={csvSending || row.status === 'done'}
                                onChange={e => setCsvRows(prev => prev.map((r, j) => j === i ? { ...r, role: e.target.value } : r))}
                                className="csv-role-select"
                              >
                                <option value="admin">Admin</option>
                                <option value="member">Member</option>
                                <option value="viewer">Viewer</option>
                              </select>
                            </td>
                            <td className="csv-status">
                              {row.status === null && <span className="csv-status-pending">Pending</span>}
                              {row.status === 'sending' && <span className="csv-status-sending">Sending…</span>}
                              {row.status === 'done' && <span className="csv-status-done"><Check size={13} /> Sent</span>}
                              {row.status === 'error' && <span className="csv-status-error"><AlertCircle size={13} /> {row.error}</span>}
                            </td>
                            <td>
                              <button
                                className="csv-remove-btn"
                                disabled={csvSending || row.status === 'done'}
                                onClick={() => setCsvRows(prev => prev.filter((_, j) => j !== i))}
                                title="Remove"
                              ><X size={13} /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="csv-actions">
                      <span className="csv-count">{csvRows.length} recipient{csvRows.length !== 1 ? 's' : ''}</span>
                      <button
                        className="invite-tab-btn"
                        disabled={csvSending || !currentUser?.orgId || csvRows.every(r => r.status === 'done')}
                        onClick={handleSendAllInvites}
                      >
                        {csvSending ? 'Sending…' : 'Send All Invites'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {recentInvites.length > 0 && (
                <div className="recent-invites">
                  <p className="recent-invites-label">Generated invite links</p>
                  {recentInvites.map((inv, i) => (
                    <div key={i} className="recent-invite-row">
                      <div className="recent-invite-info">
                        <span className="recent-invite-email">{inv.email}</span>
                        <span className="recent-invite-role">{inv.role}</span>
                        {inv.emailSent !== undefined && (
                          <span className={`email-status ${inv.emailSent ? 'sent' : 'failed'}`}>
                            {inv.emailSent ? '✓ Email sent' : '⚠ Email failed'}
                          </span>
                        )}
                      </div>
                      <div className="recent-invite-link">
                        <input type="text" readOnly value={inv.link} className="invite-link-input" />
                        <button
                          className="copy-btn"
                          onClick={() => handleCopyLink(inv.link, i)}
                          title="Copy link"
                        >
                          {copiedToken === i ? <Check size={14} /> : <Copy size={14} />}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="invite-role-guide">
                <p className="role-guide-title">Role permissions</p>
                <div className="role-guide-rows">
                  <div className="role-guide-row">
                    <span className="role-guide-name admin">Admin</span>
                    <span>Full access — manage team, settings, all data</span>
                  </div>
                  <div className="role-guide-row">
                    <span className="role-guide-name member">Member</span>
                    <span>Can view and edit deals, documents, contacts</span>
                  </div>
                  <div className="role-guide-row">
                    <span className="role-guide-name viewer">Viewer</span>
                    <span>Read-only access to all resources</span>
                  </div>
                  <div className="role-guide-row">
                    <span className="role-guide-name custom">Custom</span>
                    <span>Custom permissions — configure in Permissions tab</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Permissions Tab ── */}
          {activeTab === 'permissions' && isAdmin && (
            <div className="permissions-tab">
              {/* Sub-nav: Roles vs Members */}
              <div className="perm-subnav">
                <button
                  className={`perm-subnav-btn ${permView === 'roles' ? 'active' : ''}`}
                  onClick={() => setPermView('roles')}
                >
                  <UserCog size={14} /> Role Defaults
                </button>
                <button
                  className={`perm-subnav-btn ${permView === 'members' ? 'active' : ''}`}
                  onClick={() => setPermView('members')}
                >
                  <Users size={14} /> Per Member
                </button>
              </div>

              {/* ── Role Defaults ── */}
              {permView === 'roles' && (
                <div className="role-perms-view">
                  <p className="permissions-desc">
                    Set the default permissions for each role type. Changes apply when assigning a role.
                  </p>
                  <div className="role-perms-layout">
                    {/* Role selector */}
                    <div className="role-type-selector">
                      {Object.keys(ROLE_LABELS).map(role => (
                        <button
                          key={role}
                          className={`role-type-btn ${editingRole === role ? 'active' : ''}`}
                          onClick={() => setEditingRole(role)}
                          style={{ '--role-color': ROLE_COLORS[role] }}
                        >
                          <span className="role-type-dot" style={{ background: ROLE_COLORS[role] }} />
                          {ROLE_LABELS[role]}
                        </button>
                      ))}
                    </div>

                    {/* Permission checklist */}
                    <div className="role-perm-checklist">
                      <h4 className="role-perm-title">
                        {ROLE_LABELS[editingRole]} Permissions
                        {editingRole === 'custom' && <span className="custom-badge">Custom Role</span>}
                      </h4>
                      {editingRole === 'custom' && (
                        <p className="custom-role-hint">
                          Customize exactly what users with the "Custom" role can do.
                        </p>
                      )}
                      <div className="perm-checklist">
                        {CUSTOM_PERMISSIONS.map(({ key, label }) => (
                          <label key={key} className="perm-check-row">
                            <input
                              type="checkbox"
                              checked={!!(rolePerms[editingRole]?.[key])}
                              onChange={() => toggleRolePerm(editingRole, key)}
                              disabled={editingRole === 'admin'}
                            />
                            <span className="perm-check-label">{label}</span>
                            {editingRole === 'admin' && (
                              <span className="perm-check-locked"><Lock size={11} /> Always enabled for admins</span>
                            )}
                          </label>
                        ))}
                      </div>
                      {editingRole !== 'admin' && (
                        <button
                          className="perm-save-btn"
                          onClick={() => saveRolePermissions(editingRole)}
                          disabled={rolePermsSaving}
                        >
                          {rolePermsSaving ? 'Saving...' : `Save ${ROLE_LABELS[editingRole]} Permissions`}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Per Member ── */}
              {permView === 'members' && (
                <>
                  <p className="permissions-desc">
                    Customize per-member resource access. Select a member to edit their permissions.
                  </p>
                  <div className="perm-layout">
                    <div className="perm-member-list">
                      {members.map(member => (
                        <button
                          key={member.id}
                          className={`perm-member-btn ${selectedMemberId === member.id ? 'active' : ''}`}
                          onClick={() => loadMemberPermissions(member.id)}
                        >
                          <div className="perm-member-avatar">
                            {(member.full_name || member.email)?.[0]?.toUpperCase()}
                          </div>
                          <div className="perm-member-info">
                            <span className="perm-member-name">{member.full_name || member.email}</span>
                            <span className="perm-member-role">{ROLE_LABELS[member.role] || member.role}</span>
                          </div>
                        </button>
                      ))}
                    </div>

                    <div className="perm-grid-area">
                      {!selectedMemberId ? (
                        <div className="perm-select-prompt">
                          <Settings size={32} />
                          <p>Select a member to configure permissions</p>
                        </div>
                      ) : permLoading ? (
                        <div className="perm-loading">Loading permissions...</div>
                      ) : (
                        <>
                          <div className="perm-grid">
                            <div className="perm-grid-header">
                              <span className="perm-resource-col">Resource</span>
                              {['view', 'edit', 'delete', 'admin'].map(action => {
                                const icons = { view: Eye, edit: Edit3, delete: Trash2, admin: Lock };
                                const Icon = icons[action];
                                return (
                                  <span key={action} className="perm-action-col">
                                    <Icon size={13} />
                                    {action}
                                  </span>
                                );
                              })}
                            </div>
                            {['pipeline', 'contacts', 'documents', 'deals', 'settings', 'summaries', 'library', 'chats'].map(resource => (
                              <div key={resource} className="perm-grid-row">
                                <span className="perm-resource-name" style={{ textTransform: 'capitalize' }}>{resource}</span>
                                {['view', 'edit', 'delete', 'admin'].map(action => (
                                  <label key={action} className="perm-toggle">
                                    <input
                                      type="checkbox"
                                      checked={getPermValue(resource, action)}
                                      onChange={() => togglePerm(resource, action)}
                                    />
                                    <span className="perm-toggle-check" />
                                  </label>
                                ))}
                              </div>
                            ))}
                          </div>
                          <div className="perm-save-row">
                            <button className="perm-save-btn" onClick={savePermissions} disabled={permSaving}>
                              {permSaving ? 'Saving...' : 'Save Permissions'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
    </>
  );

  if (isInline) {
    return <div className="team-inline">{inner}</div>;
  }

  return (
    <div className="team-modal-overlay" onClick={onClose}>
      <div className="team-modal" onClick={e => e.stopPropagation()}>
        {inner}
      </div>
    </div>
  );
};

export default TeamSettings;
